import { useEffect, useState, useCallback, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Card, Button, Alert, Loader, Badge, Select, Dialog, Input, Table, ProgressBar } from '@dreamtree-org/twreact-ui';
import { useAppApi } from '@/lib/api';
import { runUpload, errText } from '@/lib/videoUpload';
import VideoPlayer from '@/components/VideoPlayer';
import Can from '@rbac/Can';

const ACCEPT = 'video/mp4,video/webm,video/quicktime';

/** The origin the embed will be served from — same origin as the SPA. */
const embedSrc = (publicId) => `${window.location.origin}/embed/${publicId}`;
// `allow` grants fullscreen but NOT picture-in-picture: PiP pops the video into a floating
// always-on-top window, which is the cleanest capture surface a browser offers. The player
// also sets disablePictureInPicture, but an embed code that requests the permission would
// be handing it out on paper.
const embedCode = (publicId) => `<iframe src="${embedSrc(publicId)}" width="640" height="360" frameborder="0" allow="fullscreen" allowfullscreen></iframe>`;

/**
 * The audit trail a shared passcode would otherwise cost you.
 *
 * Each row is one viewing session. The code shown here is the same one burned into that
 * session's watermark, so a leaked recording resolves to a row: which network it came
 * from, on what device, and when. It identifies a session, not a person — say so rather
 * than letting the panel imply more than it knows.
 */
const mmss = (secs) => {
  const s = Number(secs) || 0;
  return `${Math.floor(s / 60)}:${String(s % 60).padStart(2, '0')}`;
};

function ViewsPanel({ views }) {
  return (
    <Card className="p-4 space-y-3">
      <h2 className="font-semibold">Viewing sessions</h2>
      {views.length ? (
        <Table
          data={views}
          columns={[
            {
              key: 'viewer_email',
              label: 'Viewer',
              render: (r) => (r.viewer_email
                ? (
                  <span>
                    <span className="font-medium">{r.viewer_name || '—'}</span>
                    <span className="block text-xs opacity-70">{r.viewer_email}</span>
                  </span>
                )
                : <span className="text-xs opacity-60">anonymous (unlisted link)</span>),
            },
            { key: 'session_code', label: 'Code', render: (r) => <span className="font-mono">{r.session_code}</span> },
            { key: 'watched_secs', label: 'Watched', render: (r) => mmss(r.watched_secs) },
            { key: 'view_count', label: 'Views' },
            { key: 'origin', label: 'From', render: (r) => <span className="text-xs opacity-70 break-all">{String(r.origin || '—').slice(0, 40)}</span> },
            { key: 'ip', label: 'IP' },
            { key: 'last_seen_at', label: 'Last seen' },
          ]}
          pagination
          // Without this twreact-ui renders its own ⋮ menu of placeholder Edit/Delete/View
          // actions whose handlers only console.log — an affordance that looks live and
          // does nothing. Every Table in this app opts out unless it wires real actions.
          withAction={false}
        />
      ) : <p className="text-sm opacity-70">No one has watched this yet.</p>}
      <p className="text-xs opacity-70">
        Each row&apos;s code is watermarked into that viewing along with the viewer&apos;s
        email. If a recording leaks, read either off it and match it here. Names and emails
        are typed by the viewer and are not verified — treat them as a signed guest book,
        not proof of identity.
      </p>
    </Card>
  );
}

/** Clipboard needs a secure context; fall back to selecting the text so the copy is still possible. */
async function copy(text, onDone) {
  try {
    await navigator.clipboard.writeText(text);
    onDone('Copied');
  } catch {
    onDone('Press Ctrl/Cmd+C to copy');
  }
}

/**
 * Finish an upload that never landed.
 *
 * A row in `uploading` has a storage key and no bytes behind it. The server will re-mint a
 * presigned PUT for exactly that state (and only that state), so re-sending the file
 * completes the original row rather than creating a duplicate.
 */
function RetryPanel({ video, onDone, onError }) {
  const { fetchApi } = useAppApi();
  const [progress, setProgress] = useState(null);
  const [stage, setStage] = useState('');
  const input = useRef(null);
  const busy = progress !== null;

  useEffect(() => {
    if (!busy) return undefined;
    const warn = (e) => { e.preventDefault(); e.returnValue = ''; };
    window.addEventListener('beforeunload', warn);
    return () => window.removeEventListener('beforeunload', warn);
  }, [busy]);

  const pick = async (e) => {
    const file = e.target.files && e.target.files[0];
    if (!file) return;
    setProgress(0);
    try {
      await runUpload({ fetchApi, file, existingId: video.id, onStage: setStage, onProgress: (f) => setProgress(Math.round(f * 100)) });
    } catch (ex) {
      onError(errText(ex));
    } finally {
      setProgress(null);
      setStage('');
      await onDone();
    }
  };

  return (
    <Card className="p-4 space-y-3">
      <h2 className="font-semibold">This upload never finished</h2>
      <p className="text-sm opacity-70">
        {video.error
          ? video.error
          : 'The file was never received by storage — the tab was probably closed or the connection dropped mid-upload.'}
        {' '}Re-send the same file to finish this video; nothing else about it changes.
      </p>
      {busy ? (
        <div className="space-y-1">
          <ProgressBar value={progress} max={100} showValue striped animated />
          <p className="text-sm opacity-70">{stage}</p>
        </div>
      ) : null}
      <input ref={input} type="file" accept={ACCEPT} className="hidden" onChange={pick} />
      <Button type="button" disabled={busy} onClick={() => { input.current.value = ''; input.current.click(); }}>
        Choose file and retry
      </Button>
    </Card>
  );
}

/** The visibility control plus the copyable embed snippet. Extracted so the page itself
 *  stays readable — it holds no state of its own. */
function SharePanel({ video, onVisibility, onPasscode, onNote }) {
  const [passcode, setPasscode] = useState('');
  const [saving, setSaving] = useState(false);

  const savePasscode = async (value) => {
    setSaving(true);
    await onPasscode(value);
    setSaving(false);
    setPasscode('');
  };

  return (
    <Card className="p-4 space-y-4">
      <h2 className="font-semibold">Share</h2>

      <Select
        label="Who can watch"
        value={video.visibility}
        onChange={(e) => onVisibility(e.target ? e.target.value : e)}
        options={[
          { label: 'Private — only signed-in users with access', value: 'private' },
          { label: 'Passcode — anyone with the link and the code', value: 'passcode' },
          { label: 'Unlisted — anyone with the link', value: 'unlisted' },
        ]}
      />

      {video.visibility === 'private' ? (
        <Alert variant="warning" title="A private video will not play when embedded">
          Browsers do not send this app&apos;s session cookie to a page inside an iframe on
          another site, so a private video shows a sign-in message there rather than a
          player. Use Passcode to share with customers outside the app.
        </Alert>
      ) : null}

      {video.visibility === 'passcode' && !video.has_passcode ? (
        <Alert variant="error" title="No passcode set">
          This video is set to require a passcode but none has been saved, so nobody can
          watch it. Set one below.
        </Alert>
      ) : null}

      <div className="space-y-2">
        <Input
          label={video.has_passcode ? 'Change passcode' : 'Set passcode'}
          type="password"
          placeholder="At least 6 characters"
          value={passcode}
          onChange={(e) => setPasscode(e.target.value)}
          disabled={saving}
        />
        <div className="flex flex-wrap gap-2">
          <Button type="button" disabled={passcode.length < 6 || saving} onClick={() => savePasscode(passcode)}>
            {video.has_passcode ? 'Update passcode' : 'Save passcode'}
          </Button>
          {video.has_passcode ? (
            <Button type="button" variant="secondary" disabled={saving} onClick={() => savePasscode('')}>
              Remove passcode
            </Button>
          ) : null}
        </div>
        <p className="text-xs opacity-70">
          Give customers the link and the passcode separately. The passcode stops casual
          sharing and the player blocks downloading — but anyone who can watch a video can
          still screen-record it. Each viewing is watermarked with a code you can trace.
        </p>
      </div>

      <div className="space-y-2">
        <label className="text-sm font-medium" htmlFor="embed-code">Embed code</label>
        <textarea
          id="embed-code"
          readOnly
          rows={3}
          value={embedCode(video.public_id)}
          onFocus={(e) => e.target.select()}
          className="w-full rounded border p-2 font-mono text-xs bg-transparent"
        />
        <div className="flex flex-wrap gap-2">
          <Button type="button" onClick={() => copy(embedCode(video.public_id), onNote)}>Copy embed code</Button>
          <Button type="button" variant="secondary" onClick={() => copy(embedSrc(video.public_id), onNote)}>Copy link</Button>
        </div>
      </div>
    </Card>
  );
}

export default function VideoDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { fetchApi } = useAppApi();

  const [video, setVideo] = useState(null);
  const [views, setViews] = useState([]);
  const [playback, setPlayback] = useState(null);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState('');
  const [note, setNote] = useState('');
  const [confirmDelete, setConfirmDelete] = useState(false);

  const load = useCallback(async () => {
    try {
      const res = await fetchApi('crud', { model: 'Video', action: 'show', where: { id } });
      const row = Array.isArray(res) ? res[0] : (res && res.data) || res;
      setVideo(row && row.id ? row : null);
      setErr('');
      if (row && row.id) {
        const v = await fetchApi('crud', {
          model: 'VideoView', action: 'list', where: { video_id: row.id }, limit: 100,
          orderBy: { column: 'last_seen_at', direction: 'desc' },
        }).catch(() => []);
        setViews(Array.isArray(v) ? v : (v && v.data) || []);
      }
    } catch (ex) {
      setErr(errText(ex));
    } finally {
      setLoading(false);
    }
  }, [fetchApi, id]);

  useEffect(() => { load(); }, [load]);

  // Depend on the two primitives actually read, not the whole `video` object. setVideo
  // stores a brand-new object from every response, so `[fetchApi, video]` gave this
  // callback a new identity on every load — and since it is handed to VideoPlayer as
  // `onRefresh`, which keys an effect on it, the expiry timer there was cleared and
  // re-armed forever and could never actually fire. The re-mint feature was dead.
  const publicId = video && video.public_id;
  const isReady = Boolean(video && video.status === 'ready');

  /** Mint (or re-mint) the signed playback URL. Passed to VideoPlayer as its refresh hook. */
  const loadPlayback = useCallback(async () => {
    if (!publicId || !isReady) return null;
    try {
      const res = await fetchApi('videoPlayback', { publicId });
      setPlayback(res);
      return res;
    } catch (ex) {
      setErr(errText(ex));
      return null;
    }
  }, [fetchApi, publicId, isReady]);

  useEffect(() => { loadPlayback(); }, [loadPlayback]);

  const NOTES = {
    private: 'Now private. Only signed-in users with access can watch, and it will not play in an embed.',
    passcode: 'Now passcode-protected. Viewers need the link and the code.',
    unlisted: 'Anyone with the link can now watch this.',
  };

  const setVisibility = async (visibility) => {
    try {
      await fetchApi('crud', { model: 'Video', action: 'update', where: { id }, data: { visibility } });
      // Worth being precise: unlike the old presigned-URL design, revoking really is
      // immediate — every byte now goes through an authorised request, so an already-open
      // player stops on its next range request rather than playing on for hours.
      setNote(`${NOTES[visibility] || 'Visibility updated.'} Access changes take effect immediately.`);
      await load();
    } catch (ex) {
      setErr(errText(ex));
    }
  };

  const setPasscode = async (passcode) => {
    try {
      await fetchApi('crud', { model: 'Video', action: 'update', where: { id }, data: { passcode } });
      setNote(passcode ? 'Passcode saved.' : 'Passcode removed.');
      await load();
    } catch (ex) {
      setErr(errText(ex));
    }
  };

  const remove = async () => {
    try {
      await fetchApi('crud', { model: 'Video', action: 'delete', where: { id } });
      navigate('/videos');
    } catch (ex) {
      setErr(errText(ex));
      setConfirmDelete(false);
    }
  };

  if (loading) return <Loader text="Loading video…" />;
  if (!video) return <Alert variant="error" title="Not found">This video does not exist.</Alert>;

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-center gap-3">
        <h1 className="text-xl font-bold">{video.title}</h1>
        <Badge variant={video.status === 'ready' ? 'success' : 'warning'}>{video.status}</Badge>
        <Badge variant={video.visibility === 'private' ? 'default' : 'info'}>{video.visibility}</Badge>
      </div>

      {err ? <Alert variant="error" title="Something went wrong">{err}</Alert> : null}
      {note ? <Alert variant="info">{note}</Alert> : null}

      <Card className="p-0 overflow-hidden">
        <div className="aspect-video bg-black">
          {video.status === 'ready' && playback
            ? <VideoPlayer playback={playback} onRefresh={loadPlayback} />
            : (
              <div className="w-full h-full flex items-center justify-center text-white/70 text-sm">
                {video.status === 'ready' ? 'Preparing playback…' : `This video is ${video.status}.`}
              </div>
            )}
        </div>
      </Card>

      {video.status === 'uploading' || video.status === 'error' ? (
        <Can module="video" action="create">
          <RetryPanel video={video} onDone={load} onError={setErr} />
        </Can>
      ) : null}

      <Can module="video" action="share">
        <SharePanel video={video} onVisibility={setVisibility} onPasscode={setPasscode} onNote={setNote} />
      </Can>

      <Can module="video" action="view">
        <ViewsPanel views={views} />
      </Can>

      <Can module="video" action="delete">
        <Button variant="danger" onClick={() => setConfirmDelete(true)}>Delete video</Button>
      </Can>

      {/* `isOpen`, NOT `open`. twreact-ui's Dialog reads isOpen and returns null without it,
          so the prop name is the whole feature: with `open` this dialog silently never
          rendered and the delete button below was unreachable. */}
      <Dialog isOpen={confirmDelete} onClose={() => setConfirmDelete(false)} title="Delete this video?">
        <div className="space-y-4">
          <p>
            “{video.title}” and its stored file will be removed permanently. Any page embedding
            it will stop working.
          </p>
          <div className="flex gap-2 justify-end">
            <Button variant="secondary" onClick={() => setConfirmDelete(false)}>Cancel</Button>
            <Button variant="danger" onClick={remove}>Delete</Button>
          </div>
        </div>
      </Dialog>
    </div>
  );
}
